import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { sendSharedMessage, registerMessageHandler, useSharedWebSocket } from '../../../hooks/useSharedWebSocket';
import { useAuth } from '../../../components/auth/AuthProvider';
import { setNativeInputValue } from './setNativeInputValue';

// Connettore desktop: montato SOLO quando c'è un utente desktop loggato e NON su route mobile.
// Tiene la connessione WS condivisa aperta+autenticata come 'desktop'. Montaggio condizionale →
// non scrive currentConfig fuori contesto → niente clobber/loop del singleton.
function DesktopScanConnector({ userId, userName }: { userId: string; userName: string }) {
  useSharedWebSocket({ userId, userName, userType: 'desktop', enabled: true });
  return null;
}

// F2 attivo su QUALUNQUE campo di testo in focus (textarea o input di tipo testuale).
// Esclusi: password/tipi non testuali, readonly/disabled, e opt-out `data-remote-scan="off"`.
const TEXT_INPUT_TYPES = new Set(['text', 'search', 'tel', 'url', 'number', 'email', '']);
const isScanField = (el: Element | null): el is HTMLInputElement | HTMLTextAreaElement => {
  if (!el) return false;
  if (el.getAttribute('data-remote-scan') === 'off') return false;
  if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled;
  if (el instanceof HTMLInputElement) {
    if (el.readOnly || el.disabled) return false;
    return TEXT_INPUT_TYPES.has((el.getAttribute('type') || 'text').toLowerCase());
  }
  return false;
};

// Feedback ISTANTANEO al keypress: manipola direttamente un nodo DOM (nessun ri-render React, che
// su pagine pesanti può ritardare → l'utente crede che F2 non abbia risposto e martella il tasto).
function flashInstantAck(text: string, color = '#0ea5e9') {
  let el = document.getElementById('__remote_scan_ack') as HTMLDivElement | null;
  if (!el) {
    el = document.createElement('div');
    el.id = '__remote_scan_ack';
    el.style.cssText = 'position:fixed;top:16px;left:50%;transform:translateX(-50%);z-index:2147483601;'
      + 'color:#fff;font:600 14px system-ui,sans-serif;padding:10px 16px;border-radius:10px;'
      + 'box-shadow:0 8px 24px rgba(0,0,0,.4);pointer-events:none;transition:opacity .25s;max-width:92vw;text-align:center;';
    document.body.appendChild(el);
  }
  el.textContent = text;
  el.style.background = color;
  el.style.opacity = '1';
  const anyEl = el as unknown as { _t?: ReturnType<typeof setTimeout> };
  if (anyEl._t) clearTimeout(anyEl._t);
  anyEl._t = setTimeout(() => { if (el) el.style.opacity = '0'; }, 1400);
}

// ===== Stato sessione a livello MODULO (singleton) =====
// Condiviso da TUTTE le istanze/registrazioni di handler. Se una doppia registrazione (o un handler
// di un'istanza rimontata) intercetta il messaggio, legge comunque lo stato giusto → la scrittura
// del codice nel campo funziona sempre (era il bug: l'handler che girava vedeva active=null).
let sReqId: string | null = null;     // 'waiting' | requestId | null
let sActive = false;
let sTarget: HTMLInputElement | HTMLTextAreaElement | null = null;
// Listener UI: le istanze del provider si registrano per aggiornare il riquadro di stato.
type UiState = { pending: string | null; active: null | { requestId: string; operator?: string } };
const uiListeners = new Set<(s: UiState) => void>();
let uiState: UiState = { pending: null, active: null };
function pushUi(next: Partial<UiState>) {
  uiState = { ...uiState, ...next };
  uiListeners.forEach(l => l(uiState));
}
function resetSession() { sReqId = null; sActive = false; sTarget = null; pushUi({ pending: null, active: null }); }

type Notice = { kind: 'error' | 'info'; text: string };
const noticeListeners = new Set<(n: Notice | null) => void>();
function pushNotice(n: Notice | null) { noticeListeners.forEach(l => l(n)); }

// Handler WS registrati UNA VOLTA a livello modulo (non per-istanza) → nessun doppione possibile.
let handlersInstalled = false;
function installHandlers() {
  if (handlersInstalled) return;
  handlersInstalled = true;
  registerMessageHandler('scan_request_created', (d: any) => {
    // NON annullare se broadcastCount===0: con le notifiche push l'operatore può avere l'app CHIUSA
    // (quindi non è tra i mobile WS connessi → broadcastCount 0) e aprirla dopo la push. Teniamo
    // viva la sessione — il timeout di 5 min copre il caso "davvero nessuno". Avviso solo informativo.
    if (sReqId === 'waiting') { sReqId = d.requestId; pushUi({ pending: d.requestId }); }
    if (d.broadcastCount === 0) pushNotice({ kind: 'info', text: 'Nessun telefono in linea — inviata notifica push, in attesa che un operatore apra l’app…' });
  });
  registerMessageHandler('scan_accepted', (d: any) => {
    if (sReqId && d.requestId === sReqId) {
      sActive = true;
      pushUi({ pending: null, active: { requestId: d.requestId, operator: d.assignedTo?.userName } });
    }
  });
  registerMessageHandler('scan_result', (d: any) => {
    // Basta che il requestId combaci: se arriva un codice per la nostra sessione è attiva — anche
    // se scan_accepted è andato perso in una riconnessione WS (altrimenti sActive resterebbe falso
    // e scarteremmo i codici).
    if (d.requestId !== sReqId) return;
    if (!sActive) { sActive = true; pushUi({ pending: null, active: { requestId: d.requestId } }); }
    const target = sTarget;
    if (target && document.contains(target) && isScanField(target)) {
      setNativeInputValue(target, d.code);
      try { target.focus(); } catch { /* noop */ }
    } else if (isScanField(document.activeElement)) {
      setNativeInputValue(document.activeElement as HTMLInputElement | HTMLTextAreaElement, d.code);
    } else {
      pushNotice({ kind: 'error', text: 'Il campo di origine non è più disponibile' });
    }
  });
  registerMessageHandler('scan_stopped', (d: any) => {
    // Reset anche se scan_accepted era andato perso (sActive falso): senza questo sReqId resta
    // "incastrato" quando l'operatore finisce → F2 fa no-op e le nuove richieste non partono più
    // (fino al timeout di 90s). Il fix del bug "la nuova richiesta non raggiunge nessuno".
    if (sReqId && d.requestId === sReqId) resetSession();
  });
}

export default function RemoteScanProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const isMobileRoute = pathname.startsWith('/mobile-officina') || pathname.startsWith('/mobile-operator') || pathname.startsWith('/mobile-preventivi') || pathname.startsWith('/remote-scan');

  const [ui, setUi] = useState<UiState>(uiState);
  const [notice, setNotice] = useState<Notice | null>(null);

  // Installa gli handler una sola volta (modulo) e sottoscrive questa istanza per il riquadro UI.
  useEffect(() => {
    installHandlers();
    const ul = (s: UiState) => setUi(s);
    const nl = (n: Notice | null) => setNotice(n);
    uiListeners.add(ul); noticeListeners.add(nl);
    setUi(uiState);
    return () => { uiListeners.delete(ul); noticeListeners.delete(nl); };
  }, []);

  // Auto-dismiss notice dopo 5s.
  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 5000);
    return () => clearTimeout(t);
  }, [notice]);

  // F2 = avvia richiesta (memorizza il campo d'origine); Escape = termina.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'F2' && isScanField(document.activeElement)) {
        e.preventDefault();
        // Blocca SOLO se una scansione è ATTIVA (accettata, in corso). Se c'è solo una richiesta
        // PENDENTE non accettata (l'operatore ha rifiutato, o timeout non ancora scattato), la
        // sostituiamo con una nuova — altrimenti dopo un rifiuto F2 resterebbe bloccato e tutte le
        // richieste successive non partirebbero (fino al timeout di 90s).
        if (sActive) { flashInstantAck('📷 Scansione già in corso…'); return; }
        if (sReqId && sReqId !== 'waiting') sendSharedMessage({ type: 'scan_stop', requestId: sReqId });
        resetSession();
        flashInstantAck('📷 Richiesta di scansione inviata…');
        sTarget = document.activeElement as HTMLInputElement | HTMLTextAreaElement;
        sReqId = 'waiting'; pushUi({ pending: 'waiting' });
        const okSend = sendSharedMessage({ type: 'scan_request' });
        if (!okSend) {
          resetSession();
          flashInstantAck('⚠️ Nessuna connessione', '#dc2626');
          pushNotice({ kind: 'error', text: 'Nessuna connessione al server' });
        }
      }
      if (e.key === 'Escape' && (sActive || sReqId)) {
        // scan_stop anche se ancora in pending: i telefoni devono togliere il banner,
        // altrimenti un operatore può accettare una sessione che il desktop ha già abbandonato
        // (scansiona "nel vuoto" e i codici non compaiono mai).
        if (sReqId && sReqId !== 'waiting') sendSharedMessage({ type: 'scan_stop', requestId: sReqId });
        resetSession();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /**
   * Promemoria: F2 legge il codice col telefono.
   *
   * La funzione c'è su QUALUNQUE campo di testo, ma non lo sa nessuno: l'unico
   * posto dove è scritto è il segnaposto di un campo del magazzino, messo a mano
   * (richiesta 2026-07-31). Invece di andare a scriverlo in ogni schermata — e
   * dimenticarne la metà — lo si dice quando serve: appena un campo utilizzabile
   * riceve il fuoco.
   *
   * Una volta ogni dieci minuti per sessione: un promemoria che compare a ogni
   * clic diventa rumore e si impara a ignorarlo.
   */
  useEffect(() => {
    const CHIAVE = '__remote_scan_hint_at';
    const INTERVALLO_MS = 10 * 60 * 1000;
    const onFocus = (e: FocusEvent) => {
      if (!isScanField(e.target as Element)) return;
      // Se una richiesta è già in corso il suggerimento sarebbe fuorviante.
      if (sReqId || sActive) return;
      let ultimo = 0;
      try { ultimo = Number(sessionStorage.getItem(CHIAVE) || 0); } catch { /* storage negato */ }
      if (Date.now() - ultimo < INTERVALLO_MS) return;
      try { sessionStorage.setItem(CHIAVE, String(Date.now())); } catch { /* storage negato */ }
      flashInstantAck('Premi F2 per leggere il codice col telefono', '#334155');
    };
    window.addEventListener('focusin', onFocus);
    return () => window.removeEventListener('focusin', onFocus);
  }, []);

  // Timeout accettazione: 5 min. Con le notifiche push l'operatore può avere l'app CHIUSA e
  // metterci un po' ad aprirla/sbloccare il telefono: 90s erano troppo pochi e il desktop
  // abbandonava la richiesta prima che l'operatore la accettasse.
  useEffect(() => {
    if (!ui.pending || ui.active) return;
    const t = setTimeout(() => {
      if (sReqId && !sActive) {
        if (sReqId !== 'waiting') sendSharedMessage({ type: 'scan_stop', requestId: sReqId });
        resetSession();
        pushNotice({ kind: 'error', text: 'Nessun operatore ha accettato la richiesta' });
      }
    }, 300000);
    return () => clearTimeout(t);
  }, [ui.pending, ui.active]);

  const cancelSession = () => {
    if (sReqId && sReqId !== 'waiting') sendSharedMessage({ type: 'scan_stop', requestId: sReqId });
    resetSession();
  };

  const cardBase: React.CSSProperties = { background: '#0f172a', color: '#fff', padding: '14px 16px', borderRadius: 12, boxShadow: '0 8px 28px rgba(0,0,0,.45)', fontSize: 14, maxWidth: 340, border: '1px solid #1e40af' };
  const { pending, active } = ui;
  const hasAny = !!(active || pending || notice);

  return (
    <>
      {user?.id && !isMobileRoute && <DesktopScanConnector userId={String(user.id)} userName={user?.name || user?.email || 'Desktop'} />}
      {children}
      {hasAny && (
        <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 2147483600, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
          {notice && (
            <div style={{ ...cardBase, border: `1px solid ${notice.kind === 'error' ? '#b91c1c' : '#1e40af'}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 18 }}>{notice.kind === 'error' ? '⚠️' : 'ℹ️'}</span>
                <div style={{ fontSize: 13, color: '#e2e8f0' }}>{notice.text}</div>
              </div>
            </div>
          )}
          {(active || pending) && (
            <div style={cardBase}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 20 }}>📷</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {active ? (
                    <>
                      <div style={{ fontWeight: 700, color: '#38bdf8' }}>Scansione remota attiva</div>
                      <div style={{ fontSize: 12, color: '#cbd5e1', marginTop: 2 }}>
                        {active.operator ? `Accettata da ${active.operator}. ` : ''}Inquadra i codici sul telefono: compaiono nel campo attivo.
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontWeight: 700, color: '#fbbf24' }}>Richiesta di scansione inviata…</div>
                      <div style={{ fontSize: 12, color: '#cbd5e1', marginTop: 2 }}>In attesa che un operatore accetti sul telefono.</div>
                    </>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={cancelSession}
                  style={{ background: active ? '#dc2626' : '#334155', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer' }}
                >
                  {active ? 'Termina' : 'Annulla'} <span style={{ opacity: .7, fontSize: 11 }}>(ESC)</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
